import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../lib/api';
import { formatDateTime as _fmtDT } from '../../lib/format';
import { toast } from 'sonner';
import {
  LayoutDashboard, FolderKanban, Briefcase, Wallet, ShoppingCart, Users, AlertTriangle,
  RefreshCw, ChevronRight, TrendingUp, TrendingDown,
} from 'lucide-react';

// Indian-number formatter (Cr / Lakh).
const fmtINR = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
};
const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const Section = ({ icon: Icon, title, accent, children, testid, onClick }) => (
  <div
    data-testid={testid}
    onClick={onClick}
    className={`group relative rounded-lg border bg-white p-4 shadow-sm transition hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded ${accent}`}>
          <Icon size={16} className="text-white" />
        </span>
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">{title}</h3>
      </div>
      {onClick && <ChevronRight size={16} className="text-slate-400 group-hover:text-slate-700" />}
    </div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-sm font-semibold tabular-nums ${accent || 'text-slate-900'}`}>{value}</span>
  </div>
);

export default function ExecDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const nav = useNavigate();

  const load = async (fresh = false) => {
    if (fresh) setRefreshing(true);
    else setLoading(true);
    try {
      const r = await apiGet(`/mis/exec-summary${fresh ? '?fresh=true' : ''}`);
      setData(r);
    } catch (e) {
      toast.error('Could not load exec summary');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 10 * 60 * 1000); // 10 min auto-refresh
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return <div className="p-6 text-slate-500" data-testid="exec-dashboard-loading">Loading exec summary…</div>;
  }
  if (!data) return <div className="p-6 text-rose-600">Could not load data.</div>;

  const alerts = data.alerts || {};
  const alertCount =
    (alerts.compliance_docs_expiring_30d || 0) +
    (alerts.maintenance_overdue || 0) +
    (alerts.wos_pending_approval || 0) +
    (alerts.invoices_in_mismatch || 0);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="exec-dashboard-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-display">Executive Dashboard</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            FY {data.fy_window?.start?.slice(0, 4)}–{data.fy_window?.end?.slice(2, 4)} ·
            Generated {_fmtDT(data.as_of)}
            {data.from_cache ? ` · cached ${data.cache_age_sec}s ago` : ' · live'}
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="exec-dashboard-refresh-btn"
          className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>

      {alertCount > 0 && (
        <div
          data-testid="exec-dashboard-alerts-strip"
          className="mb-4 rounded-lg border border-rose-300 bg-rose-50 p-3 flex items-center gap-3"
        >
          <AlertTriangle size={18} className="text-rose-600 flex-shrink-0" />
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
            {alerts.compliance_docs_expiring_30d > 0 && (
              <span><b>{alerts.compliance_docs_expiring_30d}</b> compliance doc(s) expiring in 30 d</span>
            )}
            {alerts.maintenance_overdue > 0 && (
              <span><b>{alerts.maintenance_overdue}</b> machine(s) overdue for service</span>
            )}
            {alerts.wos_pending_approval > 0 && (
              <span><b>{alerts.wos_pending_approval}</b> WO(s) pending approval</span>
            )}
            {alerts.invoices_in_mismatch > 0 && (
              <span><b>{alerts.invoices_in_mismatch}</b> vendor invoice(s) in mismatch</span>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Section
          icon={FolderKanban} title="Projects" accent="bg-indigo-600"
          testid="exec-block-projects" onClick={() => nav('/app/projects')}
        >
          <Stat label="Active" value={fmtNum(data.projects.active)} />
          <Stat label="Total Contract Value" value={fmtINR(data.projects.total_contract_value)} />
          <Stat label="Weighted Avg Progress" value={`${data.projects.weighted_avg_progress_pct}%`} />
          <Stat label="Behind Schedule" value={fmtNum(data.projects.behind_schedule_count)} accent="text-amber-700" />
          <Stat label="Over Budget" value={fmtNum(data.projects.over_budget_count)} accent="text-rose-700" />
        </Section>

        <Section
          icon={Briefcase} title="Order Book" accent="bg-emerald-600"
          testid="exec-block-orderbook" onClick={() => nav('/app/crm/enquiry-tracker')}
        >
          <Stat label="Tenders in Pipeline" value={fmtNum(data.order_book.tenders_in_pipeline)} />
          <Stat label="Pipeline Value" value={fmtINR(data.order_book.pipeline_value * 1e7) /* Cr→₹ display */} />
          <Stat label="Won this FY" value={fmtNum(data.order_book.won_this_fy)} />
          <Stat label="Won Value this FY" value={fmtINR(data.order_book.won_value_this_fy * 1e7)} />
          <Stat label="Hit Rate" value={`${data.order_book.hit_rate_pct}%`} />
        </Section>

        <Section
          icon={TrendingDown} title="Receivables (AR)" accent="bg-sky-600"
          testid="exec-block-receivables" onClick={() => nav('/app/billing')}
        >
          <Stat label="Total AR" value={fmtINR(data.receivables.total_ar)} />
          <Stat label="Overdue AR" value={fmtINR(data.receivables.overdue_ar)} accent="text-rose-700" />
          <Stat label="Unbilled Certified" value={fmtINR(data.receivables.unbilled_certified_work)} />
          <Stat label="Retention from Clients" value={fmtINR(data.receivables.retention_receivable_from_clients)} />
        </Section>

        <Section
          icon={TrendingUp} title="Payables (AP)" accent="bg-rose-600"
          testid="exec-block-payables" onClick={() => nav('/app/finance/cfo-dashboard')}
        >
          <Stat label="Total AP" value={fmtINR(data.payables.total_ap)} />
          <Stat label="Overdue AP" value={fmtINR(data.payables.overdue_ap)} accent="text-rose-700" />
          <Stat label="Retention Held to Subs" value={fmtINR(data.payables.retention_held_to_subs)} />
          <Stat label="Advances Outstanding" value={fmtINR(data.payables.advances_outstanding)} />
        </Section>

        <Section
          icon={ShoppingCart} title="Procurement" accent="bg-amber-600"
          testid="exec-block-procurement" onClick={() => nav('/app/procurement')}
        >
          <Stat label="Open POs Value" value={fmtINR(data.procurement.open_pos_value)} />
          <Stat label="Open WOs Value" value={fmtINR(data.procurement.open_wos_value)} />
          <Stat label="Committed This Month" value={fmtINR(data.procurement.committed_this_month)} />
          <Stat label="Pending GRNs" value={fmtNum(data.procurement.pending_grns)} />
        </Section>

        <Section
          icon={Users} title="Workforce" accent="bg-violet-600"
          testid="exec-block-workforce" onClick={() => nav('/app/hr/dashboard')}
        >
          <Stat label="Employees" value={fmtNum(data.workforce.employees)} />
          <Stat label="Staff Present Today" value={fmtNum(data.workforce.staff_present_today)} />
          <Stat label="Labour On Site Today" value={fmtNum(data.workforce.labour_on_site_today)} />
          <Stat label="Attendance % Today" value={`${data.workforce.attendance_pct_today}%`} />
          <Stat label="Payroll this Month" value={fmtINR(data.workforce.payroll_cost_this_month)} />
        </Section>

        <Section
          icon={Wallet} title="Cash & Working Capital" accent="bg-teal-600"
          testid="exec-block-cash"
        >
          <Stat
            label="Net Working Capital (AR − AP)"
            value={fmtINR(data.receivables.total_ar - data.payables.total_ap)}
            accent={(data.receivables.total_ar - data.payables.total_ap) < 0 ? 'text-rose-700' : 'text-emerald-700'}
          />
          <Stat label="AR − AP Overdue Gap" value={fmtINR(data.receivables.overdue_ar - data.payables.overdue_ap)} />
          <p className="text-[11px] text-slate-500 italic mt-2">
            Hook up to Finance GL (coming next session) for real bank balances.
          </p>
        </Section>

        <Section
          icon={AlertTriangle} title="Alerts & Actions" accent="bg-rose-700"
          testid="exec-block-alerts"
        >
          <Stat label="Compliance docs expiring (30d)" value={fmtNum(alerts.compliance_docs_expiring_30d)} accent="text-rose-700" />
          <Stat label="Maintenance overdue" value={fmtNum(alerts.maintenance_overdue)} accent="text-amber-700" />
          <Stat label="WOs pending approval" value={fmtNum(alerts.wos_pending_approval)} />
          <Stat label="Invoices in mismatch" value={fmtNum(alerts.invoices_in_mismatch)} accent="text-rose-700" />
        </Section>

        <Section
          icon={LayoutDashboard} title="Cross-project Comparison" accent="bg-slate-700"
          testid="exec-block-compare" onClick={() => nav('/app/mis/compare')}
        >
          <p className="text-sm text-slate-600">
            Rank every active project by margin · progress · cost overrun · labour productivity.
          </p>
          <span className="inline-flex mt-2 text-xs text-indigo-700 font-medium">Open Compare Projects →</span>
        </Section>
      </div>
    </div>
  );
}
